import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Sparkles, Plus, Trash2, AlertCircle, Loader2, Info, TrendingUp, Download, ChevronDown, ChevronRight, ExternalLink, MapPin, FileText, ShieldCheck, DollarSign, Database, BarChart3, Package, Activity, Building2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import InfoHint from '../components/ui/info-hint';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import AddMaterialDialog from '../components/parts/AddMaterialDialog';
import PartHeader from '../components/parts/PartHeader';
import PartEditDrawer from '../components/edit/PartEditDrawer';
import PartLifecycleSections from '../components/parts/PartLifecycleSections';
import PartIHSLinkSection from '../components/parts/PartIHSLinkSection';
import PricePlanningSection from '../components/parts/PricePlanningSection';
import UnifiedPlanningGrid from '../components/forecast/UnifiedPlanningGrid';
import VarianceAnalysis from '../components/forecast/VarianceAnalysis';
import ScenarioSelector from '../components/scenarios/ScenarioSelector';
import CustomFieldsSection from '../components/CustomFieldsSection';
import EntityWorkflowsPanel from '../components/workflows/EntityWorkflowsPanel';
import { partsApi } from '../api/parts';
import { tariffRatesApi } from '../api/tariffRates';
import { scenariosApi } from '../api/scenarios';

export default function PartDetail() {
  const { partId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const programContextId = searchParams.get('programId');
  const [part, setPart] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [calculation, setCalculation] = useState(null);
  const [annualImpact, setAnnualImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [waterfallKey, setWaterfallKey] = useState(0); // Key to force waterfall refresh
  const [sopGridKey, setSopGridKey] = useState(0); // Key to force S&OP grid refresh
  const [totalComposition, setTotalComposition] = useState(0);
  const [error, setError] = useState(null);
  const [editingInfo, setEditingInfo] = useState(false);
  const [editedPart, setEditedPart] = useState({});
  const [saving, setSaving] = useState(false);
  const [tariffRates, setTariffRates] = useState([]);
  const [htsSearch, setHtsSearch] = useState('');
  const [showHtsDropdown, setShowHtsDropdown] = useState(false);
  const [forecastData, setForecastData] = useState([]); // Store forecast data for variance analysis
  const [scenarios, setScenarios] = useState([]);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [isTariffSectionOpen, setIsTariffSectionOpen] = useState(false); // Collapsed by default
  const [editOpen, setEditOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [programContexts, setProgramContexts] = useState([]);
  const [selectedProgramContext, setSelectedProgramContext] = useState(null);

  // Get current user role from localStorage
  useEffect(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        setUserRole(user.role || 'analyst');
      }
    } catch (e) {
      console.error('Error reading user role:', e);
      setUserRole('analyst');
    }
  }, []);

  // Handler for IHS volume sync from carline links
  const handleIHSVolumeSync = () => {
    // Force refresh the S&OP grid by changing its key
    setSopGridKey(prev => prev + 1);
  };

  const handleStartEdit = async () => {
    setEditingInfo(true);
    // Fetch tariff rates when starting edit
    await fetchTariffRates();
  };

  const handleCancelEdit = () => {
    setEditedPart({});
    setEditingInfo(false);
  };

  const fetchTariffRates = async () => {
    try {
      const response = await tariffRatesApi.getTariffRates('', '', true); // active only
      if (response.success) {
        setTariffRates(response.data.rates || []);
      }
    } catch (error) {
      console.error('Failed to fetch tariff rates:', error);
    }
  };

  const handleSaveInfo = async () => {
    setSaving(true);
    try {
      const response = await partsApi.updatePart(partId, editedPart);
      
      if (response.success) {
        // Update local part state
        setPart(response.data);
        setEditingInfo(false);
        setEditedPart({});
      } else {
        alert(response.error || 'Failed to update part');
      }
    } catch (error) {
      console.error('Failed to update part:', error);
      alert(error.message || 'Failed to update part');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchPartDetails();
    fetchScenarios();
  }, [partId, programContextId]);

  useEffect(() => {
    // Calculate total composition
    const total = materials.reduce((sum, m) => sum + (m.percentage || 0), 0);
    setTotalComposition(total);
  }, [materials]);

  useEffect(() => {
    // Close HTS dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (showHtsDropdown && !event.target.closest('.relative')) {
        setShowHtsDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showHtsDropdown]);

  const fetchPartDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await partsApi.getPartDetails(partId, { programId: programContextId });
      
      if (response.success) {
        setPart(response.data);
        setMaterials(response.data.materials || []);
        setProgramContexts(response.data.program_contexts || []);
        setSelectedProgramContext(response.data.selected_program_context || null);
        
        // Initialize edited part state
        setEditedPart({
          part_number: response.data.part_number,
          description: response.data.description,
          oem: response.data.oem,
          carline: response.data.carline,
          production_country: response.data.production_country,
          production_city: response.data.production_city,
          destination_country: response.data.destination_country,
          hts_code: response.data.hts_code,
          business_status: response.data.business_status,
          product_group: response.data.product_group,
          product_sub_group: response.data.product_sub_group,
          contract_start_date: response.data.contract_start_date,
          contract_end_date: response.data.contract_end_date,
          notes: response.data.notes
        });
        
        // Set calculation data if available
        if (response.data.per_unit_cost_cents !== null) {
          setCalculation({
            per_unit_cost_cents: response.data.per_unit_cost_cents,
            calculation_details: response.data.calculation_details,
            calculated_at: response.data.calculated_at
          });
        }
      } else {
        setError(response.error || 'Failed to load part details');
      }
    } catch (error) {
      console.error('Failed to fetch part details:', error);
      setError(error.message || 'Failed to load part details');
    } finally {
      setLoading(false);
    }
  };

  const fetchScenarios = async () => {
    try {
      const response = await scenariosApi.getScenarios(partId, { programId: programContextId });
      if (response.success) {
        setScenarios(response.data || []);
        // Set current scenario to active one or first one
        const active = response.data.find(s => s.is_active);
        setCurrentScenario(active || response.data[0] || null);
      }
    } catch (error) {
      console.error('Failed to fetch scenarios:', error);
    }
  };

  const handleScenarioChange = (scenario) => {
    setCurrentScenario(scenario);
    // The ForecastGridMonthly and PriceWaterfall components will auto-reload when currentScenario changes
  };

  const handleCreateScenario = (newScenario) => {
    setScenarios([...scenarios, newScenario]);
    setCurrentScenario(newScenario);
  };


  const handleAddMaterial = async (materialData) => {
    try {
      const response = await partsApi.addMaterial(partId, materialData);
      
      if (response.success) {
        setMaterials([...materials, response.data]);
        setShowAddMaterial(false);
        
        // Trigger recalculation if composition is now 100%
        const newTotal = materials.reduce((sum, m) => sum + m.percentage, 0) + response.data.percentage;
        if (Math.abs(newTotal - 100) < 0.01) {
          handleCalculate();
        }
      } else {
        alert(response.error || 'Failed to add material');
      }
    } catch (error) {
      console.error('Failed to add material:', error);
      alert(error.message || 'Failed to add material');
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!confirm('Are you sure you want to remove this material?')) {
      return;
    }
    
    try {
      const response = await partsApi.deleteMaterial(partId, materialId);
      
      if (response.success) {
        setMaterials(materials.filter(m => m.id !== materialId));
        // Clear calculation since composition changed
        setCalculation(null);
      } else {
        alert(response.error || 'Failed to delete material');
      }
    } catch (error) {
      console.error('Failed to delete material:', error);
      alert(error.message || 'Failed to delete material');
    }
  };

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const response = await partsApi.calculateTariff(partId);
      
      if (response.success) {
        setCalculation(response.data);
        // After calculation, fetch annual/lifetime impact
        fetchAnnualImpact();
      } else {
        alert(response.error || 'Calculation failed');
      }
    } catch (error) {
      console.error('Failed to calculate tariff:', error);
      alert(error.message || 'Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  const fetchAnnualImpact = async () => {
    setLoadingImpact(true);
    try {
      const response = await partsApi.getAnnualLifetimeImpact(partId);
      
      if (response.success) {
        setAnnualImpact(response.data);
      } else {
        setAnnualImpact(null);
      }
    } catch (error) {
      console.error('Failed to fetch impact data:', error);
      setAnnualImpact(null);
    } finally {
      setLoadingImpact(false);
    }
  };

  // Fetch impact when calculation exists
  useEffect(() => {
    if (calculation && calculation.per_unit_cost_cents) {
      fetchAnnualImpact();
    }
  }, [calculation]);

  const isCompositionValid = Math.abs(totalComposition - 100) < 0.01;

  const formatCurrency = (cents) => {
    if (cents === null || cents === undefined) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Determine impact level based on per-unit cost
  const getImpactLevel = (cents) => {
    if (!cents || cents === 0) return { level: 'none', color: 'gray', label: 'No Impact' };
    const dollars = cents / 100;
    if (dollars >= 10) return { level: 'high', color: 'red', label: 'High Impact' };
    if (dollars >= 1) return { level: 'medium', color: 'yellow', label: 'Medium Impact' };
    return { level: 'low', color: 'green', label: 'Low Impact' };
  };

  const impactLevel = calculation ? getImpactLevel(calculation.per_unit_cost_cents) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-2" />
        <div className="text-gray-500">Loading part details...</div>
      </div>
    );
  }

  if (error || !part) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">{error || 'Part not found'}</div>
      </div>
    );
  }

  // Extract material breakdown from calculation
  const materialBreakdown = calculation?.calculation_details?.material_details || [];

  // Handler for part updates from header/sections
  const handlePartUpdate = (updatedPart) => {
    setPart(prev => ({ ...prev, ...updatedPart }));
  };

  const handleProgramContextChange = (programId) => {
    const nextParams = new URLSearchParams(searchParams);
    if (programId) {
      nextParams.set('programId', programId);
    } else {
      nextParams.delete('programId');
    }
    setSearchParams(nextParams);
  };

  const formatContextVolume = (value) => (
    value ? new Intl.NumberFormat('en-US').format(value) : '-'
  );

  const formatShortDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const formatMoneyCompact = (cents) => {
    if (cents === null || cents === undefined) return '-';
    const value = Number(cents) / 100;
    if (!Number.isFinite(value)) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: Math.abs(value) >= 1000000 ? 'compact' : 'standard',
      maximumFractionDigits: Math.abs(value) >= 1000000 ? 1 : 0
    }).format(value);
  };

  const formatUnitPrice = (cents) => {
    if (cents === null || cents === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(cents) / 100);
  };

  const formatRate = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return '-';
    return `${(parsed <= 1 ? parsed * 100 : parsed).toFixed(1).replace(/\.0$/, '')}%`;
  };

  const activePriceCents = part.awarded_price_cents || part.quoted_price_cents || part.target_price_cents;
  const activePriceSource = part.awarded_price_cents ? 'awarded price' : part.quoted_price_cents ? 'quoted price' : part.target_price_cents ? 'target price' : 'no price';
  const isProgramContext = Boolean(selectedProgramContext);
  const contextPlannedVolume = selectedProgramContext?.planned_volume || part.annual_volume_commitment || 0;
  const contextRevenueCents = selectedProgramContext?.annual_revenue_cents || (contextPlannedVolume && activePriceCents ? contextPlannedVolume * activePriceCents : null);
  const headerMetrics = {
    lifecycleVolume: selectedProgramContext?.lifecycle_market_volume,
    annualVolume: contextPlannedVolume,
    weightedPrice: activePriceCents ? formatUnitPrice(activePriceCents) : '-',
    priceSource: activePriceSource,
    forecastLinks: selectedProgramContext?.market_forecast_count ?? part.ihs_links_count
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* New Part Header */}
      <PartHeader 
        part={part} 
        onUpdate={handlePartUpdate}
        onEdit={() => setEditOpen(true)}
        onBack={() => navigate(programContextId ? `/programs/${programContextId}` : '/opportunities')}
        backLabel={programContextId ? 'Back to Program 360' : 'Back to Parts'}
        selectedProgramContext={selectedProgramContext}
        metrics={headerMetrics}
      />

      <PartEditDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        part={part}
        onSaved={async (updatedPart) => {
          if (updatedPart) {
            handlePartUpdate(updatedPart);
          }
          await fetchPartDetails();
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <Card className="rounded-lg border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-blue-700">Part 360</span>
                  <Badge
                    variant="outline"
                    className={isProgramContext ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-slate-200 bg-slate-50 text-slate-600'}
                  >
                    {isProgramContext ? selectedProgramContext.program_name : 'Part Master'}
                  </Badge>
                  <InfoHint title="Part 360" side="bottom" align="start">
                    {isProgramContext
                      ? 'Market forecast basis and S&OP planning shown here are isolated to this program and do not affect other uses of this Part Master.'
                      : 'A Part Master can be used across several programs. Pick a program in Program Usage to see its forecast basis, S&OP, pricing, and claims.'}
                  </InfoHint>
                </div>
                <p className="mt-1 text-sm text-slate-600">
                  {isProgramContext
                    ? 'Volume, take rate, price, and plan for this part in this program.'
                    : 'Select a program below to review its S&OP, pricing, and claims.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {programContextId && (
                  <Button variant="outline" size="sm" onClick={() => navigate(`/programs/${programContextId}`)}>
                    Open Program 360
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => navigate('/planning')}>
                  Open S&OP Planning
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scenario Selector - kept at top so the team always knows which plan version they are working in */}
        {scenarios.length > 0 && (
          <ScenarioSelector
            scenarios={scenarios}
            currentScenario={currentScenario}
            onScenarioChange={handleScenarioChange}
            onCreateScenario={handleCreateScenario}
            onUpdate={fetchScenarios}
            partNumber={partId}
            programId={programContextId}
          />
        )}

        {/* Non-Active Scenario Warning */}
        {currentScenario && !currentScenario.is_active && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="flex-1 text-sm text-amber-800">
              <span className="font-medium text-amber-900">Draft scenario "{currentScenario.name}".</span>{' '}
              Changes stay private and do not affect the production plan.
            </p>
            <Badge variant="outline" className="border-amber-300 bg-amber-100 text-amber-800">Sandbox</Badge>
            <InfoHint title="Activate a scenario">
              To make this the production plan, open the scenario menu (three dots) and choose "Activate Scenario".
            </InfoHint>
          </div>
        )}

        {programContexts.length > 0 && (
          <Card className="rounded-lg border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    Program Usage
                    <InfoHint title="Program Usage">
                      One Part Master can appear in multiple vehicle programs. Each row owns its own forecast basis, take rate, PPV, and planning context.
                    </InfoHint>
                  </CardTitle>
                </div>
                {selectedProgramContext && (
                  <Badge variant="outline" className="w-fit border-blue-200 bg-blue-50 text-blue-700">
                    Viewing in {selectedProgramContext.program_name} context
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Program</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Platform</TableHead>
                      <TableHead>Take Rate</TableHead>
                      <TableHead>PPV / Content</TableHead>
                      <TableHead>Market Forecasts</TableHead>
                      <TableHead>Market Volume</TableHead>
                      <TableHead>Planned Volume</TableHead>
                      <TableHead className="text-right">Context</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {programContexts.map((context) => {
                      const active = context.program_id === programContextId;
                      return (
                        <TableRow key={context.program_part_id} className={active ? 'bg-blue-50/60' : ''}>
                          <TableCell>
                            <div className="font-medium text-slate-900">{context.program_name}</div>
                            <div className="text-xs text-slate-500">{context.program_code || context.program_oem}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {context.program_status || 'Not set'}
                            </Badge>
                          </TableCell>
                          <TableCell>{context.program_platform || '-'}</TableCell>
                          <TableCell>{formatRate(context.take_rate)}</TableCell>
                          <TableCell>{context.content_per_vehicle || '-'}</TableCell>
                          <TableCell>{context.market_forecast_count || 0}</TableCell>
                          <TableCell>{formatContextVolume(context.market_volume)}</TableCell>
                          <TableCell>{formatContextVolume(context.planned_volume)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant={active ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handleProgramContextChange(active ? null : context.program_id)}
                            >
                              {active ? 'Master View' : 'View Context'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <Card className="rounded-lg border-slate-200 shadow-sm xl:col-span-2">
            <CardHeader className="border-b bg-white">
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="h-4 w-4 text-blue-600" />
                {isProgramContext ? 'Program Context' : 'Part Master'}
                <InfoHint title={isProgramContext ? 'Program Context' : 'Part Master'}>
                  {isProgramContext
                    ? 'Commercial facts for this part inside the selected vehicle program.'
                    : 'Stable part data. Program-specific planning is shown only after selecting a Program 360 context.'}
                </InfoHint>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Building2 className="h-4 w-4 text-slate-500" />
                    Program & Location
                  </div>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    {isProgramContext && (
                      <>
                        <div>
                          <dt className="text-xs uppercase text-slate-500">Program</dt>
                          <dd className="font-medium text-slate-900">{selectedProgramContext.program_name || '-'}</dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase text-slate-500">Platform</dt>
                          <dd className="font-medium text-slate-900">{selectedProgramContext.program_platform || '-'}</dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase text-slate-500">OEM / Customer</dt>
                          <dd className="font-medium text-slate-900">{selectedProgramContext.program_oem || '-'}</dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase text-slate-500">SOP / EOP</dt>
                          <dd className="font-medium text-slate-900">
                            {formatShortDate(selectedProgramContext.program_sop)} / {formatShortDate(selectedProgramContext.program_eop)}
                          </dd>
                        </div>
                      </>
                    )}
                    <div>
                      <dt className="text-xs uppercase text-slate-500">Production</dt>
                      <dd className="font-medium text-slate-900">{[part.production_city, part.production_country].filter(Boolean).join(', ') || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase text-slate-500">Destination</dt>
                      <dd className="font-medium text-slate-900">{part.destination_country || '-'}</dd>
                    </div>
                  </dl>
                  {!isProgramContext && (
                    <p className="mt-3 text-xs text-slate-500">
                      Program, platform, OEM, and SOP / EOP are program-specific. Select a program in Program Usage above to view them.
                    </p>
                  )}
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <FileText className="h-4 w-4 text-slate-500" />
                    Contract & Commercial
                  </div>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <div>
                      <dt className="text-xs uppercase text-slate-500">Contract Ref</dt>
                      <dd className="font-medium text-slate-900">{part.contract_reference || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase text-slate-500">Target Price</dt>
                      <dd className="font-medium text-slate-900">{formatUnitPrice(part.target_price_cents)}</dd>
                    </div>
                    {isProgramContext && (
                      <>
                        <div>
                          <dt className="text-xs uppercase text-slate-500">Active Price</dt>
                          <dd className="font-medium text-slate-900">{formatUnitPrice(activePriceCents)}</dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase text-slate-500">Annual Revenue Basis</dt>
                          <dd className="font-medium text-slate-900">{formatMoneyCompact(contextRevenueCents)}</dd>
                        </div>
                      </>
                    )}
                    <div>
                      <dt className="text-xs uppercase text-slate-500">HTS Code</dt>
                      <dd className="font-medium text-slate-900">{part.hts_code || '-'}</dd>
                    </div>
                  </dl>
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Database className="h-4 w-4 text-slate-500" />
                    Market Forecast Basis
                  </div>
                  {isProgramContext ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                          <div className="text-xs uppercase text-slate-500">Forecast Links</div>
                          <div className="text-lg font-semibold">{selectedProgramContext.market_forecast_count || 0}</div>
                        </div>
                        <div>
                          <div className="text-xs uppercase text-slate-500">Market Volume</div>
                          <div className="text-lg font-semibold">{formatContextVolume(selectedProgramContext.market_volume)}</div>
                        </div>
                        <div>
                          <div className="text-xs uppercase text-slate-500">Source</div>
                          <div className="text-sm font-medium">IHS/S&P Mobility</div>
                        </div>
                      </div>
                      {(selectedProgramContext.market_forecasts || []).slice(0, 3).map((forecast) => (
                        <div key={forecast.core_mnemonic} className="rounded-md bg-slate-50 p-3 text-sm">
                          <div className="font-medium text-slate-900">{forecast.nameplate || forecast.program_name}</div>
                          <div className="text-slate-500">{[forecast.oem, forecast.platform, forecast.region].filter(Boolean).join(' - ')}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-600">
                      Select a Program 360 context above to see the applied vehicle forecast basis for this part.
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <BarChart3 className="h-4 w-4 text-slate-500" />
                    S&OP Summary
                  </div>
                  {isProgramContext ? (
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                      <div>
                        <dt className="text-xs uppercase text-slate-500">Take Rate</dt>
                        <dd className="font-medium text-slate-900">{formatRate(selectedProgramContext.take_rate)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase text-slate-500">PPV / Content</dt>
                        <dd className="font-medium text-slate-900">{selectedProgramContext.content_per_vehicle || '-'}</dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase text-slate-500">Planned Volume</dt>
                        <dd className="font-medium text-slate-900">{formatContextVolume(contextPlannedVolume)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase text-slate-500">Planning Scope</dt>
                        <dd className="font-medium text-green-700">Program context</dd>
                      </div>
                    </dl>
                  ) : (
                    <div className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-600">
                      S&OP is not blended at Part Master level. Choose one program usage to inspect planning basis.
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="rounded-lg border-slate-200 shadow-sm">
              <CardHeader className="border-b bg-white">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ShieldCheck className="h-4 w-4 text-blue-600" />
                  Coverage & Risk
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 p-4 text-sm">
                <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
                  <span className="text-slate-600">Market basis</span>
                  <Badge className={isProgramContext && selectedProgramContext?.market_forecast_count ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                    {isProgramContext && selectedProgramContext?.market_forecast_count ? 'Linked' : 'Needs context'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
                  <span className="text-slate-600">Price coverage</span>
                  <Badge className={activePriceCents ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                    {activePriceCents ? activePriceSource : 'Missing'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
                  <span className="text-slate-600">PPAP</span>
                  <Badge variant="outline">{part.ppap_status || 'Not set'}</Badge>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
                  <span className="text-slate-600">Tariff basis</span>
                  <Badge variant="outline">{calculation ? 'Calculated' : 'Not calculated'}</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Market forecast basis is program-specific — only shown in a program context, not on the Part Master */}
            {isProgramContext && (
              <PartIHSLinkSection
                partId={part.id}
                partNumber={partId}
                programId={programContextId}
                programContext={selectedProgramContext}
                onVolumeSync={handleIHSVolumeSync}
              />
            )}
          </div>
        </div>

        {/* S&OP planning grid is the primary planning surface - shown above price detail.
            In Part Master view (no program context) it mounts to show the identity/where-used summary. */}
        {(currentScenario || (!programContextId && programContexts.length > 0)) && (
          <UnifiedPlanningGrid
            key={sopGridKey}
            partNumber={part.part_number}
            scenarioName={currentScenario?.name || 'Active'}
            programId={programContextId}
            contextRequired={!programContextId && programContexts.length > 1}
            contextLabel="Use the Program Usage table above to select the correct vehicle program before reviewing S&OP."
            readOnly={false}
            sopDate={selectedProgramContext?.program_sop || part.sop_date}
            eopDate={selectedProgramContext?.program_eop || part.eop_date}
            onDataChange={(forecasts) => {
              setForecastData(forecasts);
              setWaterfallKey(prev => prev + 1);
            }}
          />
        )}

        {part?.id && isProgramContext && (
          <PricePlanningSection
            partId={part.id}
            part={part}
            onUpdate={fetchPartDetails}
          />
        )}

        {forecastData.some(f => Number(f.volume_actual) > 0 || Number(f.price_actual) > 0) && (
          <VarianceAnalysis forecasts={forecastData} />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CustomFieldsSection entityType="part" entityId={part.id} />
          <EntityWorkflowsPanel
            entityType="part"
            entityId={part.id}
            entityName={part.part_number || partId}
          />
        </div>

      {/* Tariff Analysis - Collapsible Section at Bottom */}
      <Collapsible open={isTariffSectionOpen} onOpenChange={setIsTariffSectionOpen}>
        <Card className="border-2 border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <CollapsibleTrigger className="w-full">
            <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isTariffSectionOpen ? (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-500" />
                  )}
                  <div className="text-left">
                    <CardTitle className="text-lg">Tariff Analysis</CardTitle>
                    <CardDescription className="text-sm">
                      Material composition and tariff calculation details
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Status Badge */}
                  {calculation ? (
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      <div className="flex items-center gap-1">
                        <span className="h-2 w-2 bg-green-600 rounded-full"></span>
                        Calculated
                      </div>
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-600 border-gray-200">
                      <div className="flex items-center gap-1">
                        <span className="h-2 w-2 bg-gray-400 rounded-full"></span>
                        Not Calculated
                      </div>
                    </Badge>
                  )}
                  {/* Composition Badge */}
                  {isCompositionValid ? (
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                      100% Composition
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                      {totalComposition.toFixed(1)}% Composition
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <CardContent className="pt-0">
              {/* Calculate Button */}
              <div className="mb-6 flex justify-end">
                <Button
                  onClick={handleCalculate}
                  disabled={calculating || !isCompositionValid}
                  className="flex items-center gap-2"
                  size="lg"
                >
                  {calculating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      Calculate Tariff
                    </>
                  )}
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: Material Composition */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">Material Composition</h3>
                    <Button
                      onClick={() => setShowAddMaterial(true)}
                      size="sm"
                      variant="outline"
                      className="flex items-center gap-1"
                    >
                      <Plus size={14} />
                      Add Material
                    </Button>
                  </div>

                  {/* Composition Progress */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Total: {totalComposition.toFixed(1)}%</p>
                      {isCompositionValid ? (
                        <Badge className="bg-green-100 text-green-800 border-green-200">
                          Valid (100%)
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                          {totalComposition === 0 ? 'No materials' : `${totalComposition.toFixed(1)}%`}
                        </Badge>
                      )}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          isCompositionValid
                            ? 'bg-green-600'
                            : totalComposition > 100
                            ? 'bg-red-500'
                            : 'bg-amber-500'
                        }`}
                        style={{ width: `${Math.min(totalComposition, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Materials Table */}
                  {materials.length === 0 ? (
                    <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <p className="text-sm text-gray-600">No materials assigned</p>
                      <p className="text-xs text-gray-500 mt-1">Add materials to calculate tariff</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Material</TableHead>
                          <TableHead className="text-right">%</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {materials.map((material) => (
                          <TableRow key={material.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium text-sm">{material.material_name}</div>
                                <div className="text-xs text-gray-500">{material.category}</div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-semibold">{material.percentage}%</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteMaterial(material.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>

                {/* Right: Calculation Results */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">Tariff Calculation Results</h3>

                  {calculation ? (
                    <div className="space-y-4">
                      {/* Result Card */}
                      <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
                        <div className="text-xs text-blue-700 mb-1">Per-Unit Tariff Cost</div>
                        <div className="text-3xl font-bold text-blue-900">
                          {formatCurrency(calculation.per_unit_cost_cents || 0)}
                        </div>
                        <div className="text-sm text-blue-600 mt-1">
                          {((calculation.effective_tariff_rate || calculation.calculation_details?.effective_tariff_rate || 0)).toFixed(2)}% effective rate
                        </div>
                      </div>

                      {/* Contract Summary */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs text-gray-600">Total Units</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {(calculation.total_units || calculation.calculation_details?.total_units || 0).toLocaleString()}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs text-gray-600">Contract Impact</div>
                          <div className="text-lg font-semibold text-gray-900">
                            ${((calculation.total_annual_impact_cents || calculation.calculation_details?.total_tariff_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="text-xs text-gray-600 space-y-1">
                        <div className="flex justify-between">
                          <span>Route:</span>
                          <span className="font-medium">{part.production_country || 'N/A'} → {part.destination_country || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>HTS Code:</span>
                          <span className="font-medium">{part.hts_code || 'Not set'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Calculated:</span>
                          <span className="font-medium">
                            {calculation.calculated_at ? new Date(calculation.calculated_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="border border-dashed border-gray-300 rounded-lg p-6">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-500 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">No calculation yet</p>
                          <p className="text-xs text-gray-600 mt-1">To calculate tariff cost:</p>
                          <ul className="text-xs text-gray-600 mt-2 ml-4 list-disc space-y-1">
                            <li>Assign materials totaling 100% {!isCompositionValid && <span className="text-amber-600 font-medium">(currently {totalComposition.toFixed(1)}%)</span>}</li>
                            <li>Ensure HTS code is set {!part.hts_code && <span className="text-amber-600 font-medium">(not set)</span>}</li>
                            <li>Add forecast data (volume & pricing)</li>
                            <li>Click "Calculate Tariff" button above</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Add Material Dialog */}
      <AddMaterialDialog
        open={showAddMaterial}
        onClose={() => setShowAddMaterial(false)}
        onAdd={handleAddMaterial}
        currentTotal={totalComposition}
      />
      </div>
    </div>
  );
}
